import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Play, Volume2, VolumeX, ChevronDown, Zap, Shield, CreditCard, Brain, Heart, Car, Building2, Search, ArrowRight } from 'lucide-react';

// Audience cards for segmentation
const audienceCards = [
  {
    id: 'ride',
    title: 'I Need a Ride',
    description: 'Book NEMT transport for medical appointments',
    icon: Heart,
    color: 'neon-cyan',
    link: 'https://ovadispatch.com/client',
    external: true,
    cta: 'Book Now'
  },
  {
    id: 'drive',
    title: 'I Want to Drive',
    description: 'Earn up to 80% of fares with flexible hours',
    icon: Car,
    color: 'neon-green',
    link: 'https://ovadispatch.com/driver',
    external: true,
    cta: 'Start Earning'
  },
  {
    id: 'provider',
    title: 'I Run a Fleet',
    description: 'Manage drivers, vehicles & credentials',
    icon: Building2,
    color: 'neon-purple',
    link: '/portal/login',
    external: false,
    cta: 'Provider Portal'
  },
  {
    id: 'broker',
    title: "I'm a Broker",
    description: 'Verify providers & access the network',
    icon: Search,
    color: 'neon-cyan',
    link: '/portal/login',
    external: false,
    cta: 'Broker Portal'
  }
];

export const HeroSection = ({ onPartnerClick }) => {
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  // Handle video autoplay properly
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Autoplay was prevented, video will stay paused
        });
      }
    }
  }, []);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  // Live stats from OVA Dispatch
  const stats = [
    { label: 'Rides Completed', value: '50K+', icon: Zap },
    { label: 'Active Drivers', value: '500+', icon: Car },
    { label: 'On-Time Rate', value: '98%', icon: Shield },
    { label: 'Avg Rating', value: '4.9', icon: Brain },
  ];

  return (
    <section 
      id="hero" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          autoPlay
          loop
          muted
          playsInline
          className="absolute w-full h-full object-cover"
          poster="https://images.unsplash.com/photo-1540886955408-0307191f4139?q=80&w=2000&auto=format&fit=crop"
        >
          <source 
            src="/videos/hero-bg.mp4" 
            type="video/mp4" 
          />
          <source 
            src="https://cdn.coverr.co/videos/coverr-driving-through-the-city-at-night-4566/1080p.mp4" 
            type="video/mp4" 
          />
        </video>
        {/* Overlay gradients - Brighter */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0c1222]/50 via-[#0c1222]/30 to-[#0c1222]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c1222]/40 via-transparent to-[#0c1222]/40" />
        
        {/* Neural network overlay effect */}
        <div className="absolute inset-0 neural-bg opacity-50" />
      </div>

      {/* Video Controls */}
      <div className="absolute bottom-8 left-8 z-20 flex items-center space-x-2">
        <button
          onClick={toggleMute}
          className="p-2 rounded-full bg-white/10 backdrop-blur-md hover:bg-white/20 transition-colors"
          data-testid="video-mute-toggle"
        >
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 backdrop-blur-md border border-white/10"
          >
            <span className="w-2 h-2 rounded-full bg-neon-green mr-2 animate-pulse" />
            <span className="text-sm font-mono text-slate-300 tracking-wide">LIVE PLATFORM • 50K+ RIDES</span>
          </motion.div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-heading font-bold text-white leading-tight">
            The First{' '}
            <span className="text-gradient">AI-Powered</span>
            <br />
            NEMT Platform
          </h1>

          {/* Subtext */}
          <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-400 leading-relaxed">
            Reliable, safe, and on-time medical transportation. Book rides, earn as a driver, 
            or manage your fleet — all in one unified ecosystem.
          </p>

          {/* Audience Segmentation Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 pt-6 max-w-5xl mx-auto"
          >
            {audienceCards.map((card, index) => {
              const Icon = card.icon;
              const CardWrapper = card.external ? 'a' : Link;
              const wrapperProps = card.external 
                ? { href: card.link, target: '_blank', rel: 'noopener noreferrer' }
                : { to: card.link };
              
              return (
                <motion.div
                  key={card.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <CardWrapper
                    {...wrapperProps}
                    className={`block glass-card p-5 text-left hover:border-${card.color}/50 transition-all duration-300 group cursor-pointer h-full`}
                    data-testid={`audience-${card.id}`}
                  >
                    <div className={`w-10 h-10 rounded-xl bg-${card.color}/20 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-5 h-5 text-${card.color}`} />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{card.title}</h3>
                    <p className="text-xs text-slate-400 mb-3">{card.description}</p>
                    <span className={`inline-flex items-center text-xs text-${card.color} group-hover:gap-2 transition-all`}>
                      {card.cta} <ArrowRight className="w-3 h-3 ml-1" />
                    </span>
                  </CardWrapper>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Secondary CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-wrap items-center justify-center gap-4 pt-2"
          >
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); onPartnerClick && onPartnerClick(); }}
              className="btn-glow text-base"
              data-testid="hero-partner"
            >
              Partner With Us
            </a>
            <a
              href="#how-it-works"
              className="btn-secondary text-base flex items-center"
              data-testid="hero-watch-system"
            >
              <Play size={16} className="mr-2" />
              See How It Works
            </a>
          </motion.div>
        </motion.div>

        {/* Live Stats from OVA Dispatch */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12 max-w-4xl mx-auto"
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 + index * 0.1 }}
              className="glass-card p-4 text-center hover:border-neon-cyan/30 transition-all duration-300 group"
              data-testid={`hero-stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <stat.icon className="w-5 h-5 mx-auto mb-2 text-neon-cyan group-hover:scale-110 transition-transform" />
              <div className="text-2xl font-heading font-bold text-white">{stat.value}</div>
              <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
      >
        <a href="#ecosystem" className="flex flex-col items-center text-slate-400 hover:text-white transition-colors">
          <span className="text-xs font-mono uppercase tracking-wider mb-2">Explore</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            <ChevronDown size={24} />
          </motion.div>
        </a>
      </motion.div>
    </section>
  );
};

export default HeroSection;
